import React, { useEffect, useRef, useState } from "react";
import styles from "./AeternaInvicta.module.scss";
import { CardFace, buildCardExamSummary } from "./AeternaInvicta";

const CARD_RENDER_HASH = "#/card-render";
const CARD_RENDER_WIDTH = "280px";
const CARD_RENDER_TIMEOUT_MS = 20000;

export function isCardRenderMode() {
  if (typeof window === "undefined") return false;
  return String(window.location.hash || "").startsWith(CARD_RENDER_HASH);
}

function cardImagesReady(root) {
  return Array.from(root.querySelectorAll("img")).every((img) => {
    const src = String(img.currentSrc || img.getAttribute("src") || "");
    return (
      Boolean(src) &&
      img.complete &&
      img.naturalWidth > 0 &&
      !src.includes("/thumbs/")
    );
  });
}

function publishResult(result) {
  window.__AI_CARD_RENDER_RESULT__ = { ready: true, ...result };
}

export default function CardRender() {
  const rootRef = useRef(null);
  const [card] = useState(() => window.__AI_CARD_RENDER__?.card || null);

  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    if (!card) {
      publishResult({ error: "No card was provided to render." });
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const startedAt = Date.now();
    const finish = async (imagesLoaded) => {
      if (document.fonts?.ready) await document.fonts.ready;
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve)),
      );
      if (cancelled) return;
      let summary = null;
      let error = null;
      try {
        summary = buildCardExamSummary(card);
      } catch (summaryError) {
        error = String(summaryError?.message || summaryError);
      }
      publishResult({ summary, imagesLoaded, error });
    };
    const check = () => {
      if (cancelled) return;
      const root = rootRef.current;
      const timedOut = Date.now() - startedAt > CARD_RENDER_TIMEOUT_MS;
      if (root && (cardImagesReady(root) || timedOut)) {
        finish(!timedOut);
        return;
      }
      timer = setTimeout(check, 100);
    };
    check();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [card]);

  return (
    <div
      className={styles.canvas}
      style={{
        display: "inline-block",
        minHeight: 0,
        padding: 16,
        background: "transparent",
      }}
    >
      <div
        ref={rootRef}
        data-card-render=""
        className={styles.cardScaleInner}
        style={{ position: "relative", "--card-focus-w": CARD_RENDER_WIDTH }}
      >
        {card ? <CardFace card={card} fullArt foilAnimateIdle={false} /> : null}
      </div>
    </div>
  );
}
